import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { KahunPanelGroup, KahunPanelSubGroup, Relations } from "shared";
import { DataInputPanel } from "../dataInputTypes";
import {
  DiagnosticAgentConfiguration,
  useDiagnosticAgentConfiguration,
} from "./config/configurationTypes";
import { Panel } from "fe-common/graphql/dao";
import {
  QualifiedFeature,
  Qualifier,
  QualifierFragment,
} from "fe-common/graphql";
import { useDiagnosticAgentState } from "../../context/diagnosticAgent";

export function panelUsesLevel(p: DataInputPanel) {
  //return categoriesWithLevel.includes(p?.category?.id as KahunCategories);
  return (
    p?.findings?.some(findingUsesLevel) ||
    p?.customFindings?.some(findingUsesLevel)
  );
}
export function findingUsesLevel(qf: QualifiedFeature) {
  return (
    qf?.feature?.qualifierSpec?.some(
      (s) => s.relation.id === Relations.LEVEL,
    ) || qf?.mandatory?.relation.id === Relations.LEVEL
  );
}

export const useDataInputPanels = () =>
  useDataInputPanelsFromConfiguration(useDiagnosticAgentConfiguration());

const filterProcedureFindings = (
  qualifiers: Qualifier[],
): QualifierFragment[] => {
  return qualifiers
    ? qualifiers.filter((q) => q.relation.id === Relations.PROCEDURE_FINDING)
    : [];
};

export async function getProcedurePanels(panelsQfs: QualifiedFeature[], dao) {
  let panelsPromises = [];
  if (panelsQfs && panelsQfs.length > 0) {
    panelsQfs.forEach((panelQf) => {
      const featureId =
        panelQf.qualifiers &&
        panelQf.qualifiers.length > 0 &&
        panelQf.qualifiers[0].value
          ? panelQf.qualifiers[0].value.id
          : panelQf.feature.id;
      panelsPromises.push(dao.searchQualifiers("", featureId, ""));
    });
  }

  const addedPanels = [];

  const values = await Promise.all(panelsPromises);
  values.forEach((qualifiers, index) => {
    const qf = panelsQfs[index];
    const procedureQualifiers = filterProcedureFindings(qualifiers);
    const feature =
      qf.qualifiers && qf.qualifiers.length > 0 && qf.qualifiers[0].value
        ? qf.qualifiers[0].value
        : qf.feature;

    const newPanel = {
      title: feature.name,
      shortname: "9999",
      id: feature.id,
      findings: procedureQualifiers
        ? procedureQualifiers.map((q) => {
            return {
              ...qf,
              feature: {
                ...qf.feature,
                ...feature,
              },
              mandatory: null,
              qualifiers: [q],
            };
          })
        : [],
      panelGroup: {
        id: KahunPanelGroup.WORKUP,
        name: KahunPanelGroup.WORKUP,
        shortname: KahunPanelGroup.WORKUP,
      },
      panelSubgroup: {
        id: KahunPanelSubGroup.Procedures,
        name: "Procedures",
        shortname: "Kahun Procedures SUB group",
      },
      customFindings: [],
    };
    addedPanels.push(newPanel);
  });

  return addedPanels;
}

const emptyPanels = new Map();

type useDataInputPanelsFromConfigurationApi = (
  config: DiagnosticAgentConfiguration,
) => {
  loading: boolean;
  panels: Map<string, DataInputPanel>;
};

export const useDataInputPanelsFromConfiguration: useDataInputPanelsFromConfigurationApi = (
  config,
) => {
  let dao = useDaoContext();

  const loadingRef = useRef<boolean>();

  let [state, setState] = useDiagnosticAgentState();

  // get the relevant panel parents
  let { dataEntryPanelID } = config;
  const [loading, setLoading] = useState(false);

  const getMainPanels = useCallback(async () => {
    // If w've got them in the state return them vs getting them again
    if (state.panels) return state.panels;
    // Make sure to only run that once per-session
    if (loadingRef.current) return;
    try {
      loadingRef.current = true;
      setLoading(true);

      const paneldata = await dao.getPanelChildren(dataEntryPanelID);
      let panels = new Map<string, DataInputPanel>(
        paneldata.map((panel) => [
          panel.id,
          {
            id: panel.id,
            title: panel.name,
            ...panel,
            findings: panel.findings?.map((f) => addPanelToQF(f, panel)),
          } as DataInputPanel,
        ]),
      );

      // Override Procedures with findings sorted smart
      const procedurePanels = Array.from(panels.values()).filter(
        (panel) => panel.panelSubgroup.id === KahunPanelSubGroup.Procedures,
      );

      let panelsPromises = [];
      procedurePanels.forEach((procedurePanel) => {
        const panelQf = procedurePanel.findings[0].feature.id;
        panelsPromises.push(dao.searchQualifiers("", panelQf, ""));
      });

      const responses = await Promise.all(panelsPromises);
      responses.forEach((qualifiers, index) => {
        const panelFeature = procedurePanels[index].findings[0].feature;
        const procedureQualifiers = filterProcedureFindings(qualifiers);
        procedurePanels[index].findings = procedureQualifiers
          ? procedureQualifiers.map((q) => {
              return {
                feature: panelFeature,
                qualifiers: [q],
              } as QualifiedFeature;
            })
          : [];
      });
      return panels;
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [setLoading, state, dao, dataEntryPanelID]);

  const getPanels = useCallback(async () => {
    const mainPanels = await getMainPanels();
    let panels = new Map(mainPanels);
    setState((state) => ({ ...state, panels }));
    setLoading(false);
  }, [getMainPanels, setState, setLoading]);

  useEffect(() => {
    // Reduce the number of dao calls to minimum for better performance
    if (dataEntryPanelID && !state.panels && !loading) {
      getPanels();
    }
  }, [loading, dataEntryPanelID, getPanels, state]);

  return { loading, panels: state.panels || emptyPanels };
};

function addPanelToQF(qf: QualifiedFeature, panel: Panel) {
  const panelQualifier: Qualifier = {
    freeText: "",
    source: null,
    name: `panel ${panel.name}`,
    negate: undefined,
    range: undefined,
    relation: {
      name: "panel",
      id: Relations.PANEL_ID,
      type: null,
      multi: false,
      essence: null
    },
    value: {
      id: panel.id,
      name: panel.name,
      shortname: panel.shortname,
      patientDescription: "",
      description: {text: ""},
      category: {
        name: panel.category?.name,
        id: panel.category?.id,
        shortname: panel.category?.shortname,
      },
      range: null,
      parents: {
        features: [],
      },
    },
  };
  let clone: QualifiedFeature = {
    ...qf,
    qualifiers: (qf.qualifiers || []).filter(
      (q) => q.relation?.id !== Relations.PANEL_ID,
    ),
  };
  clone.qualifiers.push(panelQualifier);
  return clone;
}
